import {
	BankOutlined,
	CreditCardOutlined,
	DeleteOutlined,
	DownloadOutlined,
	EditOutlined,
	EyeOutlined,
	LeftOutlined,
	PlusOutlined,
	SearchOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Button,
	DatePicker,
	Descriptions,
	Input,
	InputNumber,
	Modal,
	message,
	Popconfirm,
	Select,
	Space,
	Table,
	Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { PaymentsNavigation } from "../components/PaymentsNavigation";

import {
	deletePayment,
	getPayment,
	getPaymentApiErrorMessage,
	updatePayment,
} from "../services/payments.api";
import type { PaymentMethod, PaymentRecord } from "../types/payments.types";
import { formatUzs, paymentMethodLabels } from "../utils/payments.utils";
import { exportPaymentsToExcel, fetchAllPayments } from "../utils/payments-export";

const methodColors: Record<PaymentMethod, string> = {
	naqd: "green",
	bank: "blue",
	karta: "purple",
};

type PaymentsLocationState = {
	date?: string;
	fromDashboard?: boolean;
};

export function PaymentsPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { role } = useOutletContext<{ role: AppRole }>();
	const queryClient = useQueryClient();
	const locationState = location.state as PaymentsLocationState | null;
	const fromDashboard = locationState?.fromDashboard === true;
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | "all">("all");
	const [selectedDate, setSelectedDate] = useState<string | null>(locationState?.date || null);
	const [viewingPayment, setViewingPayment] = useState<PaymentRecord | null>(null);
	const [isExporting, setIsExporting] = useState(false);

	const [editingPayment, setEditingPayment] = useState<PaymentRecord | null>(null);
	const [editAmountUzs, setEditAmountUzs] = useState<number>(0);
	const [editDiscountAmountUzs, setEditDiscountAmountUzs] = useState<number>(0);
	const [editPaymentMethod, setEditPaymentMethod] = useState<PaymentMethod>("naqd");
	const [editPaymentDate, setEditPaymentDate] = useState<string>("");
	const [editReceiptNumber, setEditReceiptNumber] = useState<string>("");
	const [editNote, setEditNote] = useState<string>("");

	useEffect(() => {
		if (editingPayment) {
			setEditAmountUzs(editingPayment.amountUzs);
			setEditDiscountAmountUzs(editingPayment.discountAmountUzs);
			setEditPaymentMethod(editingPayment.paymentMethod);
			setEditPaymentDate(editingPayment.paymentDate);
			setEditReceiptNumber(
				editingPayment.receiptNumber === "-" ? "" : editingPayment.receiptNumber
			);
			setEditNote(editingPayment.note ?? "");
		}
	}, [editingPayment]);

	const updatePaymentMutation = useMutation({
		mutationFn: ({ id, values }: { id: string; values: any }) => updatePayment(id, values),
		onSuccess: async () => {
			message.success("To'lov yangilandi");
			setEditingPayment(null);
			await queryClient.invalidateQueries({ queryKey: ["payments"] });
			await queryClient.invalidateQueries({ queryKey: ["sales"] });
		},
		onError: (error) => {
			message.error(getPaymentApiErrorMessage(error, "To'lovni yangilashda xatolik yuz berdi"));
		},
	});

	const paymentsQuery = useQuery({
		queryKey: ["payments", selectedMethod, selectedDate],
		queryFn: () =>
			fetchAllPayments({
				dateFrom: selectedDate ?? undefined,
				dateTo: selectedDate ?? undefined,
				method: selectedMethod === "all" ? undefined : selectedMethod,
			}),
	});

	const handleExport = async () => {
		setIsExporting(true);
		try {
			const count = await exportPaymentsToExcel({
				dateFrom: selectedDate ?? undefined,
				dateTo: selectedDate ?? undefined,
				method: selectedMethod === "all" ? undefined : selectedMethod,
			});
			message.success(`${count} ta to'lov eksport qilindi`);
		} catch {
			message.error("Eksport qilishda xatolik yuz berdi");
		} finally {
			setIsExporting(false);
		}
	};

	const payments = paymentsQuery.data ?? [];

	const paymentDetailMutation = useMutation({
		mutationFn: getPayment,
		onSuccess: (data) => setViewingPayment(data),
		onError: (error) => {
			message.error(getPaymentApiErrorMessage(error, "To'lov tafsilotlarini yuklab bo'lmadi"));
		},
	});
	const deletePaymentMutation = useMutation({
		mutationFn: deletePayment,
		onSuccess: async () => {
			message.success("To'lov o'chirildi");
			await queryClient.invalidateQueries({ queryKey: ["payments"] });
			await queryClient.invalidateQueries({ queryKey: ["sales"] });
		},
		onError: (error) => {
			message.error(getPaymentApiErrorMessage(error, "To'lovni o'chirishda xatolik yuz berdi"));
		},
	});

	const filteredPayments = useMemo(() => {
		const normalizedQuery = searchQuery.toLowerCase();

		return payments.filter((payment) => {
			const matchesSearch =
				payment.clientName.toLowerCase().includes(normalizedQuery) ||
				payment.receiptNumber.toLowerCase().includes(normalizedQuery) ||
				payment.roomNumber.includes(normalizedQuery) ||
				payment.saleNumber.includes(normalizedQuery);
			const matchesMethod = selectedMethod === "all" || payment.paymentMethod === selectedMethod;
			const matchesDate = !selectedDate || payment.paymentDate === selectedDate;

			return matchesSearch && matchesMethod && matchesDate;
		});
	}, [payments, searchQuery, selectedDate, selectedMethod]);

	const totals = useMemo(() => {
		return filteredPayments.reduce(
			(acc, payment) => {
				const netAmount = payment.amountUzs - payment.refundedAmount;
				acc.total += netAmount;
				acc[payment.paymentMethod] += netAmount;
				return acc;
			},
			{ total: 0, naqd: 0, bank: 0, karta: 0 }
		);
	}, [filteredPayments]);

	const columns: ColumnsType<PaymentRecord> = [
		{
			title: "Sana",
			dataIndex: "paymentDate",
			key: "paymentDate",
			width: 116,
		},
		{
			title: "Mijoz",
			key: "client",
			render: (_, record) => (
				<div className="crm-org-name-cell">
					<span className="crm-mini-avatar green">
						{record.clientName
							.split(" ")
							.map((part) => part[0])
							.join("")
							.slice(0, 2)}
					</span>
					<div>
						<strong>{record.clientName}</strong>
						<span>Shartnoma #{record.saleNumber}</span>
					</div>
				</div>
			),
		},
		{
			title: "Xona",
			key: "room",
			render: (_, record) => (
				<div>
					<strong>{record.roomNumber}-xona</strong>
					<span className="crm-table-sub">{record.buildingName}</span>
				</div>
			),
		},
		{
			title: "Summa",
			dataIndex: "amountUzs",
			key: "amountUzs",
			align: "right",
			render: (value: number, record) => (
				<div className="crm-money-cell">
					<strong>{formatUzs(value)}</strong>
					{record.discountAmountUzs > 0 ? (
						<span>-{formatUzs(record.discountAmountUzs)} skidka</span>
					) : null}
					{record.refundedAmount > 0 ? (
						<Tag color="red" title={record.refundReason ?? undefined}>
							Qaytarilgan {formatUzs(record.refundedAmount)}
							{record.refundedAt ? ` · ${record.refundedAt.slice(0, 10)}` : ""}
						</Tag>
					) : null}
				</div>
			),
		},
		{
			title: "Usul",
			dataIndex: "paymentMethod",
			key: "paymentMethod",
			render: (value: PaymentMethod) => (
				<Tag color={methodColors[value]}>{paymentMethodLabels[value]}</Tag>
			),
		},
		{
			title: "Chek",
			dataIndex: "receiptNumber",
			key: "receiptNumber",
		},
		{
			title: "Qabul qildi",
			dataIndex: "receivedBy",
			key: "receivedBy",
		},
		{
			title: "",
			key: "actions",
			width: 104,
			render: (_, record) => (
				<Space size={4} onClick={(event) => event.stopPropagation()}>
					<Button
						aria-label="To'lov tafsilotlari"
						className="crm-users-action-btn"
						icon={<EyeOutlined />}
						loading={
							paymentDetailMutation.isPending && paymentDetailMutation.variables === record.id
						}
						size="small"
						onClick={() => paymentDetailMutation.mutate(record.id)}
					/>
					<Button
						aria-label="To'lovni tahrirlash"
						className="crm-users-action-btn"
						icon={<EditOutlined />}
						size="small"
						onClick={() => setEditingPayment(record)}
					/>
					<Popconfirm
						title="To'lovni o'chirish"
						description="Rostdan ham ushbu to'lovni o'chirmoqchimisiz?"
						overlayClassName="crm-delete-popconfirm"
						okText="Ha"
						cancelText="Yo'q"
						okButtonProps={{ danger: true }}
						onConfirm={() => deletePaymentMutation.mutate(record.id)}
					>
						<Button
							aria-label="To'lovni o'chirish"
							className="crm-users-action-btn crm-users-delete-btn"
							danger
							icon={<DeleteOutlined />}
							loading={
								deletePaymentMutation.isPending && deletePaymentMutation.variables === record.id
							}
							size="small"
						/>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="crm-payments">
			<div className="crm-page-head">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						{fromDashboard && (
							<Button
								type="text"
								icon={<LeftOutlined />}
								onClick={() => navigate(-1)}
								style={{ color: "var(--crm-text-secondary)" }}
							/>
						)}
						<h1>To'lovlar</h1>
					</div>
					<p>
						{fromDashboard ? (
							<span style={{ color: "var(--crm-text-secondary)" }}>Bosh sahifa / </span>
						) : null}
						Qabul qilingan to'lovlar kassa kesimida · {filteredPayments.length} yozuv
					</p>
				</div>
				<div className="crm-payment-actions">
					<Button icon={<DownloadOutlined />} onClick={handleExport} loading={isExporting}>
						Eksport
					</Button>
					<Button
						className="crm-primary-btn"
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => navigate("/payments/new")}
					>
						To'lov kiritish
					</Button>
				</div>
			</div>

			<PaymentsNavigation activeTab="payments" role={role} />

			<div className="crm-payment-kpis">
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon green">
							<WalletOutlined />
						</span>
						Jami qabul
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(totals.total)}</strong>
					</div>
				</div>
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon sky">
							<WalletOutlined />
						</span>
						Naqd
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(totals.naqd)}</strong>
					</div>
				</div>
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon blue">
							<BankOutlined />
						</span>
						Bank
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(totals.bank)}</strong>
					</div>
				</div>
				<div className="crm-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon violet">
							<CreditCardOutlined />
						</span>
						Karta
					</div>
					<div className="crm-kpi-value">
						<strong>{formatUzs(totals.karta)}</strong>
					</div>
				</div>
			</div>

			<div className="crm-card crm-payment-toolbar">
				<DatePicker
					placeholder="Sana"
					placement="bottomLeft"
					popupClassName="crm-compact-date-popup"
					value={selectedDate ? dayjs(selectedDate) : null}
					onChange={(_, value) => setSelectedDate(Array.isArray(value) ? null : value || null)}
				/>
				<Select
					value={selectedMethod}
					onChange={setSelectedMethod}
					options={[
						{ label: "Usul: barchasi", value: "all" },
						{ label: "Naqd", value: "naqd" },
						{ label: "Bank", value: "bank" },
						{ label: "Karta", value: "karta" },
					]}
					style={{ width: 160 }}
				/>
				<Input
					placeholder="Mijoz, xona, chek..."
					prefix={<SearchOutlined />}
					value={searchQuery}
					onChange={(event) => setSearchQuery(event.target.value)}
					style={{ maxWidth: 280 }}
				/>
			</div>

			<div className="crm-card crm-table-card">
				<Table
					columns={columns}
					dataSource={filteredPayments}
					loading={paymentsQuery.isLoading}
					locale={{
						emptyText: paymentsQuery.isError
							? "To'lovlar endpointi hozircha javob bermadi"
							: "To'lovlar topilmadi",
					}}
					rowKey="id"
					scroll={{ x: 920 }}
					pagination={{ pageSize: 8 }}
					onRow={(record) => ({
						onClick: () =>
							navigate(`/sales/${record.saleId}`, { state: { returnTo: "/payments" } }),
						style: { cursor: "pointer" },
					})}
				/>
			</div>

			<Modal
				open={viewingPayment !== null}
				onCancel={() => setViewingPayment(null)}
				footer={[
					<Button
						key="sale"
						onClick={() => {
							if (viewingPayment) {
								navigate(`/sales/${viewingPayment.saleId}`, { state: { returnTo: "/payments" } });
							}
						}}
					>
						Shartnomaga o'tish
					</Button>,
					<Button key="close" type="primary" onClick={() => setViewingPayment(null)}>
						Yopish
					</Button>,
				]}
				title="To'lov tafsilotlari"
			>
				{viewingPayment ? (
					<Descriptions column={1} size="small" bordered>
						<Descriptions.Item label="Sana">{viewingPayment.paymentDate}</Descriptions.Item>
						<Descriptions.Item label="Mijoz">{viewingPayment.clientName}</Descriptions.Item>
						<Descriptions.Item label="Shartnoma">#{viewingPayment.saleNumber}</Descriptions.Item>
						<Descriptions.Item label="Xona">
							{viewingPayment.roomNumber}-xona, {viewingPayment.buildingName}
						</Descriptions.Item>
						<Descriptions.Item label="Summa">
							{formatUzs(viewingPayment.amountUzs)}
						</Descriptions.Item>
						<Descriptions.Item label="Skidka">
							{formatUzs(viewingPayment.discountAmountUzs)}
						</Descriptions.Item>
						<Descriptions.Item label="Usul">
							{paymentMethodLabels[viewingPayment.paymentMethod]}
						</Descriptions.Item>
						<Descriptions.Item label="Chek">{viewingPayment.receiptNumber}</Descriptions.Item>
						<Descriptions.Item label="Qabul qildi">{viewingPayment.receivedBy}</Descriptions.Item>
						<Descriptions.Item label="Izoh">{viewingPayment.note ?? "-"}</Descriptions.Item>
						{viewingPayment.refundedAmount > 0 ? (
							<Descriptions.Item label="Qaytarilgan summa">
								{formatUzs(viewingPayment.refundedAmount)}
								{viewingPayment.refundedAt ? ` · ${viewingPayment.refundedAt.slice(0, 10)}` : ""}
								{viewingPayment.refundReason ? ` — ${viewingPayment.refundReason}` : ""}
							</Descriptions.Item>
						) : null}
					</Descriptions>
				) : null}
			</Modal>

			<Modal
				open={editingPayment !== null}
				onCancel={() => setEditingPayment(null)}
				title="To'lovni tahrirlash"
				okText="Saqlash"
				cancelText="Bekor qilish"
				confirmLoading={updatePaymentMutation.isPending}
				className="crm-payment-edit-modal"
				width={480}
				onOk={async () => {
					if (!editingPayment) {
						return;
					}
					updatePaymentMutation.mutate({
						id: editingPayment.id,
						values: {
							amountUzs: editAmountUzs,
							discountAmountUzs: editDiscountAmountUzs,
							paymentMethod: editPaymentMethod,
							paymentDate: editPaymentDate,
							receiptNumber: editReceiptNumber,
							note: editNote,
						},
					});
				}}
			>
				{editingPayment ? (
					<div className="crm-payment-edit-body">
						<div className="crm-payment-edit-row">
							<label className="crm-field" htmlFor="edit-payment-amount">
								<span>Summa (UZS)</span>
								<InputNumber
									id="edit-payment-amount"
									value={editAmountUzs}
									onChange={(val) => setEditAmountUzs(val ?? 0)}
									style={{ width: "100%" }}
									min={1}
									formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
									parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
								/>
							</label>
							<label className="crm-field" htmlFor="edit-payment-discount">
								<span>Chegirma (UZS)</span>
								<InputNumber
									id="edit-payment-discount"
									value={editDiscountAmountUzs}
									onChange={(val) => setEditDiscountAmountUzs(val ?? 0)}
									style={{ width: "100%" }}
									min={0}
									formatter={(value) => `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
									parser={(value) => Number((value ?? "").replace(/\s/g, ""))}
								/>
							</label>
						</div>
						<div className="crm-payment-edit-row">
							<label className="crm-field" htmlFor="edit-payment-method">
								<span>To'lov usuli</span>
								<Select
									id="edit-payment-method"
									value={editPaymentMethod}
									onChange={setEditPaymentMethod}
									options={[
										{ label: "Naqd", value: "naqd" },
										{ label: "Bank", value: "bank" },
										{ label: "Karta", value: "karta" },
									]}
									style={{ width: "100%" }}
								/>
							</label>
							<label className="crm-field" htmlFor="edit-payment-date">
								<span>Sana</span>
								<DatePicker
									id="edit-payment-date"
									value={editPaymentDate ? dayjs(editPaymentDate) : null}
									onChange={(_, dateStr) =>
										setEditPaymentDate(Array.isArray(dateStr) ? "" : dateStr || "")
									}
									style={{ width: "100%" }}
								/>
							</label>
						</div>
						<label className="crm-field" htmlFor="edit-payment-receipt">
							<span>Chek raqami</span>
							<Input
								id="edit-payment-receipt"
								value={editReceiptNumber}
								onChange={(e) => setEditReceiptNumber(e.target.value)}
							/>
						</label>
						<label className="crm-field" htmlFor="edit-payment-note">
							<span>Izoh</span>
							<Input.TextArea
								id="edit-payment-note"
								value={editNote}
								onChange={(e) => setEditNote(e.target.value)}
								rows={2}
								autoSize={{ minRows: 2, maxRows: 3 }}
							/>
						</label>
					</div>
				) : null}
			</Modal>
		</div>
	);
}

export default PaymentsPage;
